import { useCallback, useMemo, useState } from 'react';
import { XCircle } from 'react-feather';

import { Button, Input, Modal, Typography } from '@/components';
import { useTranslation } from 'react-i18next';
import { useAuthToken } from '@/hooks';
import { useCampaign, useCurationAccept, useCurationReject } from '@/hooks/client';
import { useOverlayBackHandler } from '@/hooks/useOverlayBackHandler';
import { PlatformIcon } from '@/utils/platformIcon';
import { TranslatedLink } from '@/utils/translatedComponents';
import { getDiscoveryProfileUrl } from '@/utils/discovery';
import prettyNumber from '@/utils/prettyNumber';

import styles from './Curation.module.css';

interface CurationProps {
  curation: Api.Client.Curation;
  onSuccess: TypeAny;
  tab: string;
  campaignId: string;
}

export function Curation({ curation, onSuccess, tab, campaignId }: CurationProps) {
  const [showModal, setShowModal] = useState<string | null>(null);
  const [influencer, setInfluencer] = useState<InfluencerType | null>(null);
  const [reason, setReason] = useState('');
  const { t } = useTranslation();
  const token = useAuthToken();

  const handleCloseInfluencer = useCallback(() => setInfluencer(null), []);
  const closeInfluencer = useOverlayBackHandler(!!influencer, handleCloseInfluencer);

  const { data: campaignData = {} } = useCampaign(campaignId);
  const memoCampaign = useMemo(() => campaignData || [], [campaignData]);

  const isDecline = showModal === 'decline';

  const { mutate: acceptCuration, isLoading: isAccepting } = useCurationAccept({
    curationId: curation.curation_id,
    onSuccess: () => {
      onSuccess();
      setShowModal(null);
    },
    reason
  });

  const { mutate: rejectCuration, isLoading: isRejecting } = useCurationReject({
    curationId: curation.curation_id,
    onSuccess: () => {
      onSuccess();
      setShowModal(null);
    },
    reason
  });

  const handleSubmit = (e: TypeAny) => {
    e.preventDefault();

    if (isDecline) {
      rejectCuration();
    } else {
      acceptCuration();
    }
  };

  return (
    <>
      {showModal && (
        <Modal handleClose={() => setShowModal(null)}>
          <form className={styles.curationModalForm} onSubmit={handleSubmit}>
            <Input
              onChange={(e) => setReason(e.target.value)}
              label={t(isDecline ? 'curation.modalReasonReject' : 'curation.modalReasonAccept')}
              name="reason"
              required={isDecline}
            />
            <Button
              isLoading={isAccepting || isRejecting}
              type="submit"
              theme={isDecline ? 'red' : 'cta'}
            >
              {t(isDecline ? 'curation.modalCtaReject' : 'curation.modalCtaAccept')}
            </Button>
          </form>
        </Modal>
      )}

      {influencer && (
        <div className={styles.campaignPageCollaborationsIframe} onClick={() => closeInfluencer()}>
          <button
            className={styles.campaignPageCollaborationsIframeClose}
            onClick={(e) => {
              e.stopPropagation();
              closeInfluencer();
            }}
            type="button"
          >
            <XCircle size={24} />
          </button>

          <div
            className={styles.campaignPageCollaborationsIframeSrc}
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              title={`${influencer.handle} profile`}
              className={styles.campaignPageCollaborationsIframeFrame}
              src={getDiscoveryProfileUrl(influencer.influencer_id, token)}
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
            />
          </div>
        </div>
      )}

      <div className={styles.curation} data-tab={tab} key={`curator-${curation.curation_id}`}>
        <div className={styles.curationInfluencer}>
          {curation.influencer.permalink ? (
            <button
              className={styles.curationInfluencerImage}
              onClick={() => setInfluencer(curation.influencer)}
              type="button"
            >
              <div className={styles.curationInfluencerImageContainer}>
                <img
                  className={styles.curationInfluencerImageContainerSrc}
                  src={curation.influencer.mugshot_url}
                  alt={curation.influencer.name || curation.influencer.handle}
                  // eslint-disable-next-line no-return-assign
                  onError={(e: TypeAny) => (e.target.style.opacity = 0)}
                />
              </div>

              {curation.influencer.name || curation.influencer.handle}
            </button>
          ) : (
            <div className={styles.curationInfluencerImage} style={{ color: '#000' }}>
              <div className={styles.curationInfluencerImageContainer}>
                <img
                  className={styles.curationInfluencerImageContainerSrc}
                  src={curation.influencer.mugshot_url}
                  alt={curation.influencer.name || curation.influencer.handle}
                  // eslint-disable-next-line no-return-assign
                  onError={(e: TypeAny) => (e.target.style.opacity = 0)}
                />
              </div>

              {curation.influencer.name || curation.influencer.handle}
            </div>
          )}
        </div>

        <div className={styles.curationBeat}>
          {curation.beats.map((beat) => (
            <div className={styles.curationBeatItem} key={`curation-beat-${beat.beat_id}`}>
              {beat.platform && (
                <span className={styles.curationBeatPlatform}>
                  {PlatformIcon(beat.platform, 20)}
                </span>
              )}

              <TranslatedLink href={`/connect/campaigns/${campaignId}/beat/${beat.beat_id}`}>
                {beat.beat_name}
              </TranslatedLink>
            </div>
          ))}
        </div>

        <div className={styles.curationInfluencerFollowers}>
          {curation.influencer.followers_count
            ? prettyNumber(curation.influencer.followers_count)
            : '-'}
        </div>

        <div className={styles.curationInfluencerEngagement}>
          {curation.influencer.engagement_rate
            ? `${(curation.influencer.engagement_rate * 100).toFixed(2)}%`
            : '-'}
        </div>

        <div className={styles.curationApproval}>
          {['CURATION', 'REJECTED'].includes(tab) && (
            <>
              {tab === 'CURATION' && (
                <Button
                  disabled={isAccepting || memoCampaign.is_demo}
                  onClick={() => setShowModal('decline')}
                  size="small"
                  theme="red"
                >
                  {t('campaignPageCuration.reject')}
                </Button>
              )}

              <Button
                disabled={memoCampaign.is_demo}
                onClick={() => setShowModal('accept')}
                isLoading={isAccepting}
                size="small"
                theme="cta"
              >
                {t('campaignPageCuration.accept')}
              </Button>
            </>
          )}
        </div>
      </div>

      {(() => {
        // Check if notes exist and contain actual content (not just whitespace, tabs, returns, or empty HTML)
        if (!curation.influencer.notes) return null;

        // Strip HTML tags and check if there's actual text content
        const textContent = curation.influencer.notes
          .replace(/<[^>]*>/g, '') // Remove HTML tags
          .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
          .replace(/&[a-z]+;/gi, '') // Remove other HTML entities
          .trim();

        // Only render if there's actual content
        if (!textContent) return null;

        return (
          <div className={styles.curationNotes}>
            <Typography sizes={['body-small', 'bold']}>
              {`${t('campaignPageCuration.notes')}: `}
            </Typography>
            <span
              dangerouslySetInnerHTML={{
                __html: curation.influencer.notes
              }}
            />
          </div>
        );
      })()}
    </>
  );
}
